/**
 * Types and helpers for the persisted task registry: the single task-status
 * vocabulary, "mcp-task" vs "long-task" kinds, and pure guards/derivations shared
 * by the polling hook and the UI (ADR 050).
 */
// longTask.ts imports only a *type* from this module, so this value import back
// is erased on its side and creates no runtime cycle.

const NON_TERMINAL_STATUS_VALUES = ["pending", "working"] as const;
export const TERMINAL_STATUS_VALUES = [
  "completed",
  "failed",
  "cancelled",
] as const;

const TASK_STATUS_VALUES = [
  ...NON_TERMINAL_STATUS_VALUES,
  ...TERMINAL_STATUS_VALUES,
] as const;

export type TaskStatusValue = (typeof TASK_STATUS_VALUES)[number];

/** A single result link from a fetch envelope, surfaced on the terminal card. */
export interface ResultLink {
  label: string;
  permalink: string;
}

/** The terminal subset of the status vocabulary. */
export type TerminalTaskStatus = (typeof TERMINAL_STATUS_VALUES)[number];

const NON_TERMINAL_STATUS_SET: ReadonlySet<string> = new Set(
  NON_TERMINAL_STATUS_VALUES
);

const TERMINAL_STATUS_SET: ReadonlySet<string> = new Set(
  TERMINAL_STATUS_VALUES
);

/**
 * True when a job has stopped running, so nothing is left to poll for. Tests
 * non-terminal membership rather than terminal membership so an unrecognized
 * status counts as stopped: treating an unknown value as live would poll it
 * forever.
 *
 * Use {@link isKnownTerminalStatus} instead when the result feeds an API typed
 * on `TerminalTaskStatus`; that one rejects an unrecognized status rather than
 * calling it terminal.
 *
 * The parameter is widened past `TaskStatusValue` because unrecognized input is
 * the case this guard exists to absorb: `Task.status` is parsed from an
 * unvalidated server row, so the closed union does not describe what arrives.
 */
export function shouldStopPolling(
  status: TaskStatusValue | (string & {})
): boolean {
  return !NON_TERMINAL_STATUS_SET.has(status);
}

/**
 * Narrows an unvalidated status to the terminal subset, so a caller can hand it
 * to the terminal APIs typed on that subset (`applyLongTaskTerminal`,
 * `buildMcpTaskTerminalInjection`) without a redundant re-check.
 *
 * Tests terminal membership positively, so it is sound for any input: an
 * out-of-vocabulary value is rejected rather than asserted terminal.
 */
export function isKnownTerminalStatus(
  status: unknown
): status is TerminalTaskStatus {
  return typeof status === "string" && TERMINAL_STATUS_SET.has(status);
}

/**
 * Visual state rendered by `TaskStatus` and `TaskListEntry`. A poll-exhausted
 * non-terminal entry is "stalled"; a completed + pollExhausted entry stays
 * "completed" because terminal state takes priority over stalled.
 */
export type TaskDisplayState =
  | "working"
  | "stalled"
  | "completed"
  | "failed"
  | "cancelled";

export function deriveTaskDisplayState(
  status: TaskStatusValue,
  pollExhausted: boolean
): TaskDisplayState {
  if (pollExhausted && !shouldStopPolling(status)) return "stalled";
  if (status === "completed") return "completed";
  if (status === "failed" || status === "cancelled") return status;
  return "working";
}

/**
 * Which action a card's primary button performs. Mapped to the matching handler
 * by the component, so the pure presentation logic carries no closures.
 */
type TaskCardAction = "long-task-fetch" | "long-task-status" | "mcp-task-check";

interface TaskCardPresentation {
  /**
   * Which visual treatment the card takes. The component maps each state to its
   * CSS-module classes; this module stays style-free.
   */
  tone: TaskDisplayState;
  label: string;
  buttonLabel: string;
  action: TaskCardAction;
  /** True when the primary button should not render at all. */
  suppressButton: boolean;
}

/**
 * Pure presentation logic for the `TaskStatus` card: maps the derived display
 * state (plus long-task/result context) to the label, button copy, visual tone,
 * and which action the button triggers. Returns semantic tokens only; the
 * component resolves tone to CSS classes and action to a handler.
 */
export function deriveTaskCardPresentation(
  displayState: TaskDisplayState,
  isLongTask: boolean,
  hasResults: boolean,
  fetchInFlight: boolean
): TaskCardPresentation {
  if (displayState === "completed") {
    return {
      tone: "completed",
      label: "Task complete:",
      buttonLabel: isLongTask ? "Fetch results" : "Check Result",
      action: isLongTask ? "long-task-fetch" : "mcp-task-check",
      suppressButton: isLongTask && (hasResults || fetchInFlight),
    };
  }
  if (displayState === "failed" || displayState === "cancelled") {
    return {
      tone: displayState,
      label: displayState === "cancelled" ? "Task cancelled:" : "Task failed:",
      buttonLabel: isLongTask ? "Retry fetch" : "Check Failure",
      action: isLongTask ? "long-task-fetch" : "mcp-task-check",
      suppressButton: false,
    };
  }
  return {
    tone: displayState,
    label: displayState === "stalled" ? "Task stalled:" : "Task working:",
    buttonLabel: "Check Status",
    action: isLongTask ? "long-task-status" : "mcp-task-check",
    suppressButton: false,
  };
}

/**
 * Text for a long-task card's detail row when the job has reported no
 * `statusReason` (ADR 059, "Fixed rows on the card"). The row always renders, so
 * the placeholder has to name why it is empty, and one string cannot: a terminal
 * job will not be checked again, and a stalled one has stopped polling until the
 * user clicks Refresh, so neither can claim a check is coming.
 */
export function taskDetailPlaceholder(displayState: TaskDisplayState): string {
  if (displayState === "working") return "Awaiting next status check";
  if (displayState === "stalled") return "Status checks paused — use Refresh";
  return "No status detail reported";
}

interface TaskCardTimestamp {
  /** Names the event the time refers to, e.g. "as of" → "as of 1:56:42 PM". */
  label: string;
  iso: string;
}

/**
 * Everything that could fill a long-task card's timestamp row, best candidate
 * first. Returns a list rather than one value because the row always renders
 * (ADR 059, "Fixed rows on the card"): the caller formats these in turn and
 * takes the first that parses, so a corrupt stored timestamp falls through to
 * the submit time instead of dropping the row.
 *
 * A stalled job keeps the "as of" label. Polling has paused, but the time shown
 * is still the last status check.
 *
 * A terminal job reads that same `statusCachedAt` under a "finished at" label,
 * because the status check that observed the terminal state is the closest thing
 * to a finish time the row carries. Deliberately NOT `terminalSeenAt`: that is
 * the read receipt stamped when the user opens the thread (`useMarkTerminalSeen`),
 * so it would report when the result was viewed rather than when the job ended.
 */
export function deriveTaskCardTimestamps(args: {
  displayState: TaskDisplayState;
  createdAt: string;
  statusCachedAt?: string;
}): TaskCardTimestamp[] {
  const { displayState, createdAt, statusCachedAt } = args;
  const submitted: TaskCardTimestamp = {
    label: "submitted at",
    iso: createdAt,
  };
  if (!statusCachedAt) return [submitted];

  if (displayState === "working" || displayState === "stalled") {
    return [{ label: "as of", iso: statusCachedAt }, submitted];
  }
  return [{ label: "finished at", iso: statusCachedAt }, submitted];
}

const TASK_STATUS_SET: ReadonlySet<string> = new Set(TASK_STATUS_VALUES);

/** Guards the MCP-task poller against persisting an unrecognized status string. */
export function isTaskStatusValue(status: unknown): status is TaskStatusValue {
  return typeof status === "string" && TASK_STATUS_SET.has(status);
}

export type TaskKind = "mcp-task" | "long-task";

/**
 * Builds the chat prompt for an MCP task's manual action button, keyed by
 * status. Shared by `TaskStatus` and `TaskListEntry` for consistency.
 */
export function mcpTaskActionMessage(
  status: TaskStatusValue,
  toolName: string,
  taskId: string
): string {
  if (status === "completed") {
    return `Please check the result of the ${toolName} tool (task ID: ${taskId}).`;
  }
  if (status === "failed" || status === "cancelled") {
    return `Please check the failure details of the ${toolName} tool (task ID: ${taskId}).`;
  }
  return `Please check the status of the ${toolName} tool (task ID: ${taskId}).`;
}

/**
 * Long-task analog of {@link mcpTaskActionMessage}. Long-task jobs are
 * identified by job_id (not an MCP task ID), so the prompt phrasing differs.
 * Shared by `TaskStatus` and `TaskListEntry`.
 *
 * `jobId` and `correlationId` come from the submit envelope, but neither is marked
 * untrusted: both are opaque identifiers rather than free-form prose, so there is
 * nothing in them for a marker to contain. `TaskListEntry` appends this text as a
 * turn the user sees, so a delimiter pair around each identifier would be visible
 * noise as well.
 */
export function buildLongTaskActionMessage(
  status: TaskStatusValue,
  jobId: string,
  correlationId: string | undefined
): string {
  if (status === "completed") {
    const correlationSuffix = correlationId
      ? `, correlation_id: ${correlationId}`
      : "";
    return `Please fetch and present the results for my job (job_id: ${jobId}${correlationSuffix}).`;
  }
  if (status === "failed" || status === "cancelled") {
    return `My job (job_id: ${jobId}) failed. Please report the failure reason.`;
  }
  return `What's the current status of my job (job_id: ${jobId})?`;
}

/** Defaults to "mcp-task" for entries persisted before the kind field existed. */
export function getTaskKind(entry: { kind?: TaskKind }): TaskKind {
  return entry.kind ?? "mcp-task";
}

/**
 * Workflow-agnostic completion text, so a second workflow inherits the
 * signalling with no string edit. Shared by the sidebar dot and the clipboard
 * button, whose own accessible name must carry this state.
 */
export function completionDotLabel(failed: boolean): string {
  return failed ? "Background job failed" : "Background job completed";
}
